import { Fragment, ReactNode } from 'react';
import { Box, BoxProps, Tag } from '@bitrise/bitkit';
import { InstallableArtifact } from '@/models/Build';
import ArtifactTypeFilter from '@/models/ArtifactTypeFilter';

export interface InstallableArtifactsProps extends BoxProps {
  artifacts?: InstallableArtifact[];
}

const Item = (props: { children: ReactNode; iconName: 'Android' | 'AppleFill' }) => {
  const { children, iconName } = props;
  return (
    <Tag cursor="text" iconColor="neutral.50" iconName={iconName} paddingInlineStart="3" size="sm">
      {children}
    </Tag>
  );
};

const getGroupedAndroidLabel = (items: InstallableArtifact[]) => {
  let lastExtensionType = '';
  let hasBothType = false;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const extension = (item.title?.split('.').pop() || '').toLowerCase();
    if (lastExtensionType !== '' && extension !== lastExtensionType) {
      hasBothType = true;
      break;
    }
    lastExtensionType = extension;
  }

  return hasBothType ? 'APK/AAB' : lastExtensionType?.toUpperCase() || 'APK';
};

const InstallableArtifacts = (props: InstallableArtifactsProps) => {
  const { artifacts, ...rest } = props;

  if (!artifacts || artifacts.length === 0) {
    return null;
  }

  const separatedByType: Record<ArtifactTypeFilter, InstallableArtifact[]> = {
    [ArtifactTypeFilter.APK]: [],
    [ArtifactTypeFilter.IPA]: [],
  };
  artifacts.forEach((artifact) => {
    separatedByType[artifact.artifactType].push(artifact);
  });

  return (
    <Box display="flex" gap="8" zIndex="1" {...rest}>
      {(Object.keys(separatedByType) as (keyof typeof separatedByType)[]).map((type) => {
        const items = separatedByType[type];
        let iconName: 'AppleFill' | 'Android' = 'AppleFill';
        let groupedAndroidLabel = 'APK';

        if (type === ArtifactTypeFilter.APK) {
          iconName = 'Android';
          groupedAndroidLabel = getGroupedAndroidLabel(items);
        }

        if (items.length === 0) {
          return null;
        }

        if (items.length < 3) {
          return (
            <Fragment key={type}>
              {items.map(({ id, versionCode, versionName }) => {
                if (!versionName) {
                  return null;
                }
                return (
                  <Item iconName={iconName} key={id}>
                    v{versionName} {versionCode ? ` (${versionCode})` : ''}
                  </Item>
                );
              })}
            </Fragment>
          );
        }
        return (
          <Item iconName={iconName} key={type}>
            {type === ArtifactTypeFilter.APK ? groupedAndroidLabel : 'IPA'} ({items.length})
          </Item>
        );
      })}
    </Box>
  );
};

export default InstallableArtifacts;
